import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Wand2,
  Sparkles,
  Gauge,
  Layers,
  Palette as PaletteIcon,
  Paintbrush,
  PaintBucket,
  Pencil,
  Activity,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Recommendations } from "@/lib/imageProcessing";
import { PAINT_EFFECT_OPTIONS, type PaintEffectType } from "@/lib/postProcessing";
import { ARTISTIC_EFFECT_OPTIONS, type ArtisticEffectType } from "@/lib/artisticEffects";

interface ParametersPanelProps {
  numColors: number;
  onNumColorsChange: (value: number) => void;
  minRegionSize: number;
  onMinRegionSizeChange: (value: number) => void;
  smoothness: number;
  onSmoothnessChange: (value: number) => void;
  mergeTolerance: number;
  onMergeToleranceChange: (value: number) => void;
  enableArtisticMerge: boolean;
  onEnableArtisticMergeChange: (value: boolean) => void;
  smartPalette: boolean;
  onSmartPaletteChange: (value: boolean) => void;
  paintEffect: PaintEffectType;
  onPaintEffectChange: (effect: PaintEffectType) => void;
  paintIntensity: number;
  onPaintIntensityChange: (intensity: number) => void;
  artisticEffect: ArtisticEffectType;
  onArtisticEffectChange: (effect: ArtisticEffectType) => void;
  artisticIntensity: number;
  onArtisticIntensityChange: (intensity: number) => void;
  profilingEnabled: boolean;
  onProfilingEnabledChange: (enabled: boolean) => void;
  analysisReady: boolean;
  recommendations: Recommendations | null;
  onProcess: () => void;
  isProcessing: boolean;
}

/* --- helpers UI locaux, zéro dépendance --- */
function Section({
  title,
  icon,
  badge,
  children,
  defaultOpen = true,
  summaryHelp,
}: {
  title: string;
  icon?: React.ReactNode;
  badge?: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
  summaryHelp?: string;
}) {
  return (
    <details
      className="group border border-border/50 rounded-lg bg-card/60 open:bg-card transition-colors"
      {...(defaultOpen ? { open: true } : {})}
    >
      <summary className="flex items-center justify-between gap-1.5 cursor-pointer list-none px-1.5 py-1 rounded-lg hover:bg-accent/50">
        <div className="flex items-center gap-1.5 min-w-0">
          <div className="p-1 rounded-md bg-primary/10 text-primary">{icon}</div>
          <span className="truncate text-xs font-medium">{title}</span>
          {summaryHelp ? (
            <span className="hidden md:inline text-[10px] text-muted-foreground truncate">
              {summaryHelp}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-1.5">
          {badge}
          <span
            aria-hidden="true"
            className="i-chevron transition-transform group-open:rotate-180 text-muted-foreground"
          />
        </div>
      </summary>
      <div className="px-1.5 pb-1.5 pt-0.5">{children}</div>
    </details>
  );
}

export const ParametersPanel = ({
  numColors,
  onNumColorsChange,
  minRegionSize,
  onMinRegionSizeChange,
  smoothness,
  onSmoothnessChange,
  mergeTolerance,
  onMergeToleranceChange,
  enableArtisticMerge,
  onEnableArtisticMergeChange,
  smartPalette,
  onSmartPaletteChange,
  paintEffect,
  onPaintEffectChange,
  paintIntensity,
  onPaintIntensityChange,
  artisticEffect,
  onArtisticEffectChange,
  artisticIntensity,
  onArtisticIntensityChange,
  profilingEnabled,
  onProfilingEnabledChange,
  analysisReady,
  recommendations,
  onProcess,
  isProcessing,
}: ParametersPanelProps) => {
  const quality =
    numColors <= 0
      ? { label: "Non défini", className: "bg-slate-700" }
      : numColors <= 15
      ? { label: "Simple", className: "bg-blue-700" }
      : numColors <= 25
      ? { label: "Détaillé", className: "bg-purple-700" }
      : { label: "Artistique", className: "bg-pink-700" };

  return (
    <Card className="p-1.5 space-y-1.5 rounded-lg border bg-card text-card-foreground shadow-sm">
      {/* Header panel */}
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold flex items-center gap-1.5">
          <div className="p-1 rounded-md bg-primary/10">
            <Wand2 className="h-3.5 w-3.5 text-primary" />
          </div>
          Paramètres de génération
        </h3>
        <Badge className={`${quality.className} text-white text-[10px] px-1.5 py-0.5`}>
          {quality.label}
        </Badge>
      </div>
      <div className="text-[10px] text-muted-foreground">
        {analysisReady
          ? "Analyse terminée : vous pouvez ajuster les paramètres puis lancer le traitement."
          : "Analyse requise : lancez l'analyse brute pour obtenir des recommandations."}
      </div>

      {/* SECTION: Qualité & fusion de base */}
      <Section
        title="Qualité & fusion"
        icon={<Sparkles className="h-3.5 w-3.5" />}
        summaryHelp="Nombre de couleurs et simplification des petites zones"
        defaultOpen
        badge={
          <span className="text-xs font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-full">
            {numColors} col.
          </span>
        }
      >
        <fieldset className="space-y-2" aria-labelledby="colors-legend">
          <legend id="colors-legend" className="sr-only">
            Nombre de couleurs
          </legend>

          {/* Nombre de couleurs */}
          <div className="space-y-1">
            <div className="flex justify-between items-center">
              <Label htmlFor="colors" className="flex items-center gap-1.5 text-xs">
                <Sparkles className="h-3 w-3 text-primary" />
                Nombre de couleurs
              </Label>
              <span className="text-[10px] font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded-full">
                {numColors}
              </span>
            </div>
            {recommendations && (
              <div className="text-[9px] text-muted-foreground">
                Recommandation&nbsp;: {recommendations.recommendedNumColors} couleurs
              </div>
            )}
            <Slider
              id="colors"
              min={0}
              max={80}
              step={1}
              value={[numColors]}
              onValueChange={(v) => onNumColorsChange(v[0])}
              aria-label="Nombre de couleurs"
              className="w-full [&_.relative]:h-1 [&_[role=slider]]:h-3.5 [&_[role=slider]]:w-3.5 [&_[role=slider]]:border-2 [&_[role=slider]]:shadow-sm"
            />
            <div className="flex justify-between text-[9px] text-muted-foreground">
              <span>0 (Non défini)</span>
              <span>80 (Complexe)</span>
            </div>
          </div>

          {/* Fusion zones (min region) */}
          <div className="space-y-1">
            <div className="flex justify-between items-center">
              <Label htmlFor="region" className="flex items-center gap-1.5 text-xs">
                <Layers className="h-3 w-3 text-primary" />
                Fusion des petites zones
              </Label>
              <span className="text-[10px] font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded-full">
                {minRegionSize} px
              </span>
            </div>
            {recommendations && (
              <div className="text-[9px] text-muted-foreground">
                Recommandation&nbsp;: {recommendations.recommendedMinRegionSize}px
              </div>
            )}
            <Slider
              id="region"
              min={0}
              max={500}
              step={10}
              value={[minRegionSize]}
              onValueChange={(v) => onMinRegionSizeChange(v[0])}
              aria-label="Fusion zones"
              className="w-full [&_.relative]:h-1 [&_[role=slider]]:h-3.5 [&_[role=slider]]:w-3.5 [&_[role=slider]]:border-2 [&_[role=slider]]:shadow-sm"
            />
            <div className="flex justify-between text-[9px] text-muted-foreground">
              <span>0 (Non défini)</span>
              <span>500 (Simplifié)</span>
            </div>
          </div>

          {/* Douceur des bords */}
          <div className="space-y-1">
            <div className="flex justify-between items-center">
              <Label htmlFor="smoothness" className="flex items-center gap-1.5 text-xs">
                <Gauge className="h-3 w-3 text-primary" />
                Douceur des bords
              </Label>
              <span className="text-[10px] font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded-full">
                {smoothness}%
              </span>
            </div>
            <Slider
              id="smoothness"
              min={0}
              max={100}
              step={5}
              value={[smoothness]}
              onValueChange={(v) => onSmoothnessChange(v[0])}
              aria-label="Douceur des bords"
              className="w-full [&_.relative]:h-1 [&_[role=slider]]:h-3.5 [&_[role=slider]]:w-3.5 [&_[role=slider]]:border-2 [&_[role=slider]]:shadow-sm"
            />
            <div className="flex justify-between text-[9px] text-muted-foreground">
              <span>0 (Net)</span>
              <span>100 (Doux)</span>
            </div>
          </div>
        </fieldset>
      </Section>

      {/* SECTION: Fusion artistique */}
      <Section
        title="Fusion artistique"
        icon={<Sparkles className="h-3.5 w-3.5" />}
        summaryHelp="Ajuste la tolérance ΔE quand activée"
        defaultOpen={false}
        badge={
          <span className="text-xs text-muted-foreground">
            {enableArtisticMerge ? "Activée" : "Désactivée"}
          </span>
        }
      >
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-1.5 text-xs" htmlFor="art-merge">
              <Sparkles className="h-3 w-3 text-primary" />
              Activer la fusion artistique
            </Label>
            <Switch
              id="art-merge"
              checked={enableArtisticMerge}
              onCheckedChange={onEnableArtisticMergeChange}
              aria-label="Activer la fusion artistique"
            />
          </div>

          {enableArtisticMerge && (
            <div className="space-y-1 pl-4">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                  <Gauge className="h-2.5 w-2.5 text-primary/70" />
                  Tolérance ΔE
                </Label>
                <span className="text-[10px] font-semibold text-primary bg-primary/10 px-1.5 py-0.5 rounded-full">
                  {mergeTolerance}
                </span>
              </div>
              <Slider
                min={0}
                max={30}
                step={1}
                value={[mergeTolerance]}
                onValueChange={(v) => onMergeToleranceChange(v[0])}
                aria-label="Tolérance ΔE"
                className="w-full [&_.relative]:h-1 [&_[role=slider]]:h-3.5 [&_[role=slider]]:w-3.5 [&_[role=slider]]:border-2 [&_[role=slider]]:shadow-sm"
              />
            </div>
          )}
        </div>
      </Section>

      {/* SECTION: Palette & effets peinture */}
      <Section
        title="Palette & Effets"
        icon={<PaletteIcon className="h-3.5 w-3.5" />}
        summaryHelp="Palette intelligente et rendu peinture"
        defaultOpen={false}
      >
        {/* Palette intelligente */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label
              htmlFor="smart-palette"
              className="flex items-center gap-1.5 text-xs cursor-pointer"
            >
              <PaletteIcon className="h-3 w-3 text-primary" />
              <div className="flex flex-col">
                <span>Palette intelligente</span>
                <span className="text-[9px] text-muted-foreground font-normal">
                  Équilibrage chromatique automatique
                </span>
              </div>
            </Label>
            <Switch
              id="smart-palette"
              checked={smartPalette}
              onCheckedChange={onSmartPaletteChange}
              aria-label="Activer la palette intelligente"
            />
          </div>
        </div>

        {/* Effet peinture */}
        <div className="space-y-1.5 pt-1.5 border-t border-border/40">
          <Label className="flex items-center gap-1.5 text-xs" htmlFor="paint-effect">
            <Paintbrush className="h-3 w-3 text-primary" />
            Effet peinture
          </Label>

          <Select value={paintEffect} onValueChange={(value) => onPaintEffectChange(value as PaintEffectType)}>
            <SelectTrigger id="paint-effect" className="h-8 text-xs">
              <SelectValue placeholder="Sélectionner…" />
            </SelectTrigger>
            <SelectContent>
              {PAINT_EFFECT_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {paintEffect !== "none" && (
            <div className="space-y-1 pl-4">
              <div className="flex justify-between items-center">
                <Label className="text-[10px] text-muted-foreground">Intensité</Label>
                <span className="text-[10px] font-semibold text-primary bg-primary/10 px-1.5 py-0.5 rounded-full">
                  {paintIntensity}%
                </span>
              </div>
              <Slider
                min={0}
                max={100}
                step={5}
                value={[paintIntensity]}
                onValueChange={(v) => onPaintIntensityChange(v[0])}
                aria-label="Intensité de l'effet peinture"
                className="w-full [&_.relative]:h-1 [&_[role=slider]]:h-3.5 [&_[role=slider]]:w-3.5 [&_[role=slider]]:border-2 [&_[role=slider]]:shadow-sm"
              />
            </div>
          )}
        </div>
      </Section>

      {/* SECTION: Effets artistiques (AI) */}
      <Section
        title="Effets artistiques (AI)"
        icon={<PaintBucket className="h-3.5 w-3.5" />}
        summaryHelp="Filtres style huile/crayon et intensité"
        defaultOpen={false}
      >
        <div className="space-y-1.5">
          <Label className="flex items-center gap-1.5 text-xs" htmlFor="ai-effect">
            <PaintBucket className="h-3 w-3 text-primary" />
            Choix de l'effet
          </Label>

          <Select
            value={artisticEffect}
            onValueChange={(value) => onArtisticEffectChange(value as ArtisticEffectType)}
          >
            <SelectTrigger id="ai-effect" className="h-8 text-xs">
              <SelectValue placeholder="Sélectionner…" />
            </SelectTrigger>
            <SelectContent>
              {ARTISTIC_EFFECT_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {artisticEffect !== "none" && (
            <div className="space-y-1 pl-4">
              <div className="flex justify-between items-center">
                <Label className="text-[10px] text-muted-foreground">Intensité</Label>
                <span className="text-[10px] font-semibold text-primary bg-primary/10 px-1.5 py-0.5 rounded-full">
                  {artisticIntensity}%
                </span>
              </div>
              <Slider
                min={0}
                max={100}
                step={5}
                value={[artisticIntensity]}
                onValueChange={(v) => onArtisticIntensityChange(v[0])}
                aria-label="Intensité de l'effet artistique"
                className="w-full [&_.relative]:h-1 [&_[role=slider]]:h-3.5 [&_[role=slider]]:w-3.5 [&_[role=slider]]:border-2 [&_[role=slider]]:shadow-sm"
              />
            </div>
          )}
        </div>
      </Section>

      {/* SECTION: Performance / Profileur */}
      <Section
        title="Performance"
        icon={<Activity className="h-3.5 w-3.5" />}
        summaryHelp="Active la mesure des étapes du pipeline"
        defaultOpen={false}
        badge={
          <span className="text-xs text-muted-foreground">
            {profilingEnabled ? "Profilage actif" : "Profilage inactif"}
          </span>
        }
      >
        <div className="flex items-center justify-between">
          <Label
            htmlFor="profiling"
            className="flex items-center gap-1.5 text-xs cursor-pointer"
          >
            <Activity className="h-3 w-3 text-primary" />
            <div className="flex flex-col">
              <span>Activer le profileur</span>
              <span className="text-[9px] text-muted-foreground font-normal">
                Mesure les performances du pipeline
              </span>
            </div>
          </Label>
          <Switch
            id="profiling"
            checked={profilingEnabled}
            onCheckedChange={onProfilingEnabledChange}
            aria-label="Activer le profileur de performance"
          />
        </div>
      </Section>

      {/* CTA Générer (parité fonctionnelle) */}
      <Button
        onClick={onProcess}
        disabled={isProcessing || !analysisReady}
        className="w-full h-8 text-xs rounded-md bg-primary text-primary-foreground shadow hover:opacity-90 disabled:opacity-60"
        size="sm"
        aria-live="polite"
        aria-busy={isProcessing}
      >
        {isProcessing ? (
          <>
            <span className="mr-1.5 inline-block h-2.5 w-2.5 animate-spin rounded-full border-2 border-primary-foreground/80 border-t-transparent" />
            Traitement en cours…
          </>
        ) : (
          <>
            <Wand2 className="mr-1.5 h-3.5 w-3.5" />
            Lancer le traitement
          </>
        )}
      </Button>
      {!analysisReady && (
        <div className="text-[9px] text-muted-foreground text-center">
          Analyse nécessaire avant génération.
        </div>
      )}
    </Card>
  );
};
